// src/index.ts
import { z as z3 } from "zod";

// src/defs.ts
import { z } from "zod";
var CANONICAL_MODES = [
  "Underway",
  "Anchored",
  "Moored",
  "Docked",
  "Maintenance",
  "Sailing"
];
var alertSeveritySchema = z.enum([
  "emergency",
  "alarm",
  "warning",
  "caution"
]);
var notificationUrgencySchema = z.enum([
  "silent",
  "local",
  "remote",
  "escalating"
]);
var alertOriginSchema = z.enum(["ftue", "local", "cloud"]);
var CUSTOM_RECIPE_PREFIX = "custom:";
var customTriggerClassSchema = z.enum([
  "High",
  "Low",
  "On",
  "Off",
  "DataUnavailable"
]);
var customSpecSchema = z.object({
  v: z.literal(1),
  path: z.string().min(1).max(256),
  triggerClass: customTriggerClassSchema
});
var alertConditionClauseSchema = z.union([
  z.object({
    preset: z.literal("engine_running"),
    minRpm: z.number().positive().optional()
  }),
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("High"),
      path: z.string().min(1).max(256),
      value: z.number()
    }),
    z.object({
      type: z.literal("Low"),
      path: z.string().min(1).max(256),
      value: z.number()
    }),
    z.object({ type: z.literal("Active"), path: z.string().min(1).max(256) }),
    z.object({ type: z.literal("Inactive"), path: z.string().min(1).max(256) })
  ])
]);
var alertConditionsSchema = z.object({
  logic: z.enum(["all", "any"]),
  clauses: z.array(alertConditionClauseSchema).min(1).max(4)
});
var alertBindingSchema = z.object({
  path: z.string().min(1).max(256)
});
var alertDefinitionSchema = z.object({
  id: z.uuid(),
  recipeId: z.string(),
  title: z.string(),
  severity: alertSeveritySchema,
  enabled: z.boolean(),
  enabledModes: z.array(z.string()),
  thresholdsByMode: z.record(z.string(), z.record(z.string(), z.number())),
  notificationUrgencyByMode: z.record(z.string(), notificationUrgencySchema),
  sourcePaths: z.array(z.string()),
  /**
   * @deprecated Dead weight — the compiler never reads it. Retained only so its
   * removal would not churn `defsDigest`; its schema is intentionally UNCHANGED.
   * To pin a device/instance, use {@link alertBindingSchema} via `binding.path`,
   * which supersedes this field.
   */
  deviceIds: z.array(z.string()),
  /**
   * A custom (non-recipe) alarm's spec. Present ⇒ this def is custom (see
   * {@link isCustomDef}); absent ⇒ a recipe def. Optional so adding it never
   * churns an existing recipe def's `defsDigest`.
   */
  custom: customSpecSchema.optional(),
  /**
   * Optional qualifier conditions AND/OR-ed with the trigger before the alarm
   * fires (e.g. "only while the engine is running"). Absent ⇒ unconditional.
   */
  conditions: alertConditionsSchema.optional(),
  /**
   * Optional concrete-instance path pin. Absent ⇒ the compiler's family
   * first-match ("any"). Supersedes the deprecated `deviceIds`.
   */
  binding: alertBindingSchema.optional(),
  explanation: z.string().optional(),
  origin: alertOriginSchema,
  updatedAt: z.string()
});
var retiredDefIdentitySchema = z.object({
  id: z.string().max(128),
  recipeId: z.string().max(120),
  updatedAt: z.string()
});
function isCustomDef(def) {
  return def.custom !== void 0;
}
function customRecipeIdFor(defId) {
  return CUSTOM_RECIPE_PREFIX + defId;
}

// src/modes.ts
var CANONICAL_BY_LOWER = new Map(
  CANONICAL_MODES.map((m) => [m.toLowerCase(), m])
);
function foldModesToCanonical(modes) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const raw of modes) {
    const canonical = CANONICAL_BY_LOWER.get(raw.toLowerCase());
    if (canonical === void 0 || seen.has(canonical)) continue;
    seen.add(canonical);
    out.push(canonical);
  }
  return out;
}
var SAILING = "Sailing";
var UNDERWAY = "Underway";
function foldSailingOntoUnderway(modes) {
  if (!modes.includes(UNDERWAY) || modes.includes(SAILING)) return [...modes];
  return [...modes, SAILING];
}
function foldModeKeys(byMode) {
  const out = {};
  for (const [rawMode, value] of Object.entries(byMode)) {
    const canonical = CANONICAL_BY_LOWER.get(rawMode.toLowerCase());
    if (canonical === void 0) continue;
    out[canonical] = value;
  }
  return out;
}
function modesAreCanonical(modes) {
  return modes.every((m) => CANONICAL_BY_LOWER.get(m.toLowerCase()) === m);
}
function foldDefinitionModes(def) {
  const enabledModes = foldModesToCanonical(def.enabledModes);
  const droppedAllModes = def.enabledModes.length > 0 && enabledModes.length === 0;
  return {
    def: {
      ...def,
      enabledModes,
      thresholdsByMode: foldModeKeys(def.thresholdsByMode),
      notificationUrgencyByMode: foldModeKeys(def.notificationUrgencyByMode)
    },
    droppedAllModes
  };
}

// src/recipes.ts
import { z as z2 } from "zod";

// src/engine.ts
var ENGINE_RUNNING_RPM = 300;
var ENGINE_TURNING_RPM = 50;
function finite(rpm) {
  return typeof rpm === "number" && Number.isFinite(rpm);
}
function isEngineRunning(rpm) {
  return finite(rpm) && rpm > ENGINE_RUNNING_RPM;
}
function isEngineTurning(rpm) {
  return finite(rpm) && rpm > ENGINE_TURNING_RPM;
}
var ENGINE_SPEED_RESERVED_FLOOR_RPM = 8191;
function isReportedEngineSpeed(rpm) {
  return typeof rpm === "number" && Number.isFinite(rpm) && rpm >= 0 && rpm < ENGINE_SPEED_RESERVED_FLOOR_RPM;
}

// src/digest.ts
function sortKeysDeep(value) {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => [k, sortKeysDeep(v)])
    );
  }
  return value;
}
function canonicalJson(value) {
  return JSON.stringify(sortKeysDeep(value));
}
function defsDigest(defs) {
  const stripped = defs.map(({ updatedAt: _updatedAt, ...rest }) => rest).sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  return sha256Hex(canonicalJson(stripped));
}
function rulesDigest(rules) {
  return sha256Hex(canonicalJson(rules));
}
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
function rotr(x, n) {
  return (x >>> n | x << 32 - n) >>> 0;
}
function toHex8(x) {
  return (x >>> 0).toString(16).padStart(8, "0");
}
function sha256Hex(message) {
  const msg = new TextEncoder().encode(message);
  const bitLen = msg.length * 8;
  const withOne = msg.length + 1;
  const pad = (56 - withOne % 64 + 64) % 64;
  const total = withOne + pad + 8;
  const buf = new Uint8Array(total);
  buf.set(msg);
  buf[msg.length] = 128;
  const dv = new DataView(buf.buffer);
  dv.setUint32(total - 4, bitLen >>> 0, false);
  dv.setUint32(total - 8, Math.floor(bitLen / 4294967296), false);
  let h0 = 1779033703, h1 = 3144134277, h2 = 1013904242, h3 = 2773480762;
  let h4 = 1359893119, h5 = 2600822924, h6 = 528734635, h7 = 1541459225;
  const w = new Uint32Array(64);
  for (let i = 0; i < total; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = dv.getUint32(i + t * 4, false);
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ w[t - 15] >>> 3;
      const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ w[t - 2] >>> 10;
      w[t] = w[t - 16] + s0 + w[t - 7] + s1 >>> 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g;
      const temp1 = h + S1 + ch + K[t] + w[t] >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const temp2 = S0 + maj >>> 0;
      h = g;
      g = f;
      f = e;
      e = d + temp1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = temp1 + temp2 >>> 0;
    }
    h0 = h0 + a >>> 0;
    h1 = h1 + b >>> 0;
    h2 = h2 + c >>> 0;
    h3 = h3 + d >>> 0;
    h4 = h4 + e >>> 0;
    h5 = h5 + f >>> 0;
    h6 = h6 + g >>> 0;
    h7 = h7 + h >>> 0;
  }
  return toHex8(h0) + toHex8(h1) + toHex8(h2) + toHex8(h3) + toHex8(h4) + toHex8(h5) + toHex8(h6) + toHex8(h7);
}

// src/n2k-identity.ts
var N2K_ALERT_TYPE = Object.freeze({
  emergency: 1,
  // Emergency Alarm
  alarm: 2,
  // Alarm
  warning: 5,
  // Warning
  caution: 8
  // Caution
});
var N2K_CATEGORY_NAVIGATIONAL = 0;
var N2K_CATEGORY_TECHNICAL = 1;
var NAVIGATIONAL_RECIPE_IDS = /* @__PURE__ */ new Set([
  "gps_loss",
  "gps_source_mismatch",
  "anchor_drift",
  "shallow_depth",
  // The anchor-depth pair reads the same sounder as `shallow_depth` and answers
  // the same navigational question, so it takes the same category. A miss here
  // is silent — the alarm still annunciates, as Technical — which is why the
  // list is written per recipe rather than inferred.
  "anchor_depth_low",
  "deep_depth"
]);
var NAVIGATIONAL_CUSTOM_PATH_PREFIXES = [
  "navigation.",
  "environment.depth"
];
function alertCategoryFor(def) {
  if (isCustomDef(def) && def.custom !== void 0) {
    return NAVIGATIONAL_CUSTOM_PATH_PREFIXES.some(
      (prefix) => def.custom.path.startsWith(prefix)
    ) ? N2K_CATEGORY_NAVIGATIONAL : N2K_CATEGORY_TECHNICAL;
  }
  return NAVIGATIONAL_RECIPE_IDS.has(def.recipeId) ? N2K_CATEGORY_NAVIGATIONAL : N2K_CATEGORY_TECHNICAL;
}
var NEOMARIS_MONITOR_ALERT_SYSTEM = 1;
var N2K_PRIORITY_BY_SEVERITY = Object.freeze({
  emergency: 10,
  alarm: 20,
  warning: 50,
  caution: 80
});
var ALERT_ID_MODULUS = 65533;
var SUBSYSTEM_MODULUS = 253;
function n2kAlertIdentity(def) {
  const hash = sha256Hex(def.id);
  const alertNumericId = parseInt(hash.slice(0, 8), 16) % ALERT_ID_MODULUS;
  const alertSubSystem = parseInt(hash.slice(8, 10), 16) % SUBSYSTEM_MODULUS;
  return {
    alertType: N2K_ALERT_TYPE[def.severity],
    alertCategory: alertCategoryFor(def),
    alertSystem: NEOMARIS_MONITOR_ALERT_SYSTEM,
    alertSubSystem,
    alertNumericId,
    alertPriority: N2K_PRIORITY_BY_SEVERITY[def.severity]
  };
}

// src/recipe-instance.ts
var INSTANCE_SEP = "#";
var MAX_RECIPE_ID_LEN = 120;
function parseRecipeId(recipeId) {
  const at = recipeId.indexOf(INSTANCE_SEP);
  if (at === -1) return { base: recipeId, instance: void 0 };
  return { base: recipeId.slice(0, at), instance: recipeId.slice(at + 1) };
}
function mintRecipeId(base, pinnedPath) {
  if (pinnedPath === void 0 || pinnedPath.length === 0) return base;
  if (pinnedPath.includes(INSTANCE_SEP)) {
    throw new Error(
      `pinned path may not contain '${INSTANCE_SEP}': ${pinnedPath}`
    );
  }
  const minted = `${base}${INSTANCE_SEP}${pinnedPath}`;
  if (minted.length > MAX_RECIPE_ID_LEN) {
    throw new Error(
      `recipe id would exceed ${MAX_RECIPE_ID_LEN} chars (${minted.length}): ${minted}`
    );
  }
  return minted;
}

// src/recipes.ts
var CLOUD_NOTIFY_URGENCIES = /* @__PURE__ */ new Set([
  "remote",
  "escalating"
]);
function notificationRoutesFor(def) {
  const wantsRemote = def.enabledModes.some((mode) => {
    const urgency = def.notificationUrgencyByMode[mode];
    return urgency !== void 0 && CLOUD_NOTIFY_URGENCIES.has(urgency);
  });
  return wantsRemote ? [{ channel: "email" }] : void 0;
}
function toDisplayValue(spec, stored) {
  switch (spec.display?.unit) {
    case "seconds":
      return stored / 1e3;
    case "minutes":
      return stored / 6e4;
    case "percent":
      return stored * 100;
    default:
      return stored;
  }
}
function fromDisplayValue(spec, display) {
  switch (spec.display?.unit) {
    case "seconds":
      return Math.round(display * 1e3);
    case "minutes":
      return Math.round(display * 6e4);
    case "percent":
      return display / 100;
    default:
      return display;
  }
}
var DEFAULT_ENGINE_RUNNING_RPM = ENGINE_RUNNING_RPM;
function familyOf(re) {
  return { source: re.source, flags: re.flags };
}
function fixedFamily(path) {
  return {
    source: `^${path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
    flags: "",
    singleMember: true
  };
}
var MIN_CORE_FOR_SPLIT_RECIPES = "0.5.2";
var MIN_CORE_FOR_ANCHOR_DEPTH = "999.0.0";
var MIN_CORE_FOR_CYCLE_COUNT = "0.9.1";
var MIN_CORE_FOR_BATTERY_SOC = "0.10.3";
var MIN_CORE_FOR_CAMERAS = "0.10.11";
var MIN_CORE_FOR_DETECTOR_CONTACTS = "0.10.19";
var REACTIVATION_PERIOD_SECONDS_BY_SEVERITY = {
  // Fire, gas, flooding, oil pressure. Acknowledging must never mean forgetting.
  emergency: 300,
  // A real hazard, but half an hour is long enough to have acted on one.
  alarm: 1800,
  // Advisory. Acknowledged means the operator has it — stop asking.
  warning: 0,
  caution: 0
};
var TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY = {
  emergency: 60,
  alarm: 300,
  warning: 600,
  caution: 600
};
var CLEAR_DELAY_SECONDS = 0;
var BATTERY_HYSTERESIS_V = 0.2;
var ENGINE_TEMP_HYSTERESIS_C = 3;
var OIL_HYSTERESIS_FACTOR = 1.1;
var DEPTH_HYSTERESIS_FACTOR = 1.1;
var TANK_HYSTERESIS_PCT = 5;
var BATTERY_SOC_HYSTERESIS_PCT = 2;
var FRIDGE_HYSTERESIS_C = 2;
var ANCHOR_CLEAR_FACTOR = 0.9;
var KPA_TO_HPA = 10;
var RATIO_TO_PERCENT = 100;
function round2(n) {
  return Math.round(n * 100) / 100;
}
function msToSeconds(ms) {
  return Math.round(ms / 1e3);
}
function pending(reason) {
  return { pending: true, reason };
}
function stripDeviceSuffix(p) {
  const at = p.indexOf("@");
  return at >= 0 ? p.slice(0, at) : p;
}
function finiteNumber(params, key) {
  const v = params[key];
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
function primaryParams(def) {
  for (const m of def.enabledModes) {
    const p = def.thresholdsByMode[m];
    if (p) return { mode: m, params: p };
  }
  const keys = Object.keys(def.thresholdsByMode).sort();
  if (keys.length > 0)
    return { mode: keys[0], params: def.thresholdsByMode[keys[0]] };
  return void 0;
}
function setDelayFrom(params, key = "debounceMs") {
  const v = finiteNumber(params, key);
  return v === void 0 ? 0 : msToSeconds(v);
}
function resolvePinnedPath(def, catalog, fits) {
  const pinned = def.binding?.path;
  if (pinned === void 0) return void 0;
  if (!fits(pinned)) {
    return { reason: `bound path does not fit this alarm (${pinned})` };
  }
  if (!catalog.has(pinned)) {
    return { reason: `bound path not reported: ${pinned}` };
  }
  return { path: pinned };
}
function resolvePath(def, catalog, familyRe, prefer) {
  const fits = (p) => familyRe.test(p);
  const pinned = resolvePinnedPath(def, catalog, fits);
  if (pinned) return pinned;
  for (const raw of def.sourcePaths) {
    const p = stripDeviceSuffix(raw);
    if (catalog.has(p) && fits(p)) return { path: p };
  }
  const matches = [...catalog].filter(fits).sort();
  if (prefer) {
    const preferred = matches.find((p) => prefer.test(p));
    if (preferred) return { path: preferred };
  }
  return matches.length > 0 ? { path: matches[0] } : {};
}
function resolveFixed(def, catalog, path) {
  const pinned = resolvePinnedPath(def, catalog, (p) => p === path);
  if (pinned) return pinned;
  return catalog.has(path) ? { path } : {};
}
function buildRule(def, shape) {
  const identity = n2kAlertIdentity(def);
  const notificationRoutes = notificationRoutesFor(def);
  const rule = {
    alertId: def.id,
    inputPath: shape.inputPath,
    class: shape.ruleClass,
    trigger: shape.trigger,
    enabledModes: foldSailingOntoUnderway(def.enabledModes),
    setDelaySeconds: shape.setDelaySeconds,
    clearDelaySeconds: shape.clearDelaySeconds ?? CLEAR_DELAY_SECONDS,
    reactivationPeriodSeconds: REACTIVATION_PERIOD_SECONDS_BY_SEVERITY[def.severity],
    temporarySilencePeriodSeconds: TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY[def.severity],
    // Inert since ADR-086 — see `compiledRuleSchema`. Still emitted so
    // `rulesDigest` is stable across the change.
    acknowledgeSupport: true,
    temporarySilenceSupport: true,
    severity: def.severity,
    title: def.title,
    recipeId: def.recipeId,
    notificationUrgencyByMode: { ...def.notificationUrgencyByMode },
    // Cloud notification routing (P8). Present only when an enabled mode asks
    // for remote/escalating urgency — the boat's build-dispatch keys on it.
    ...notificationRoutes !== void 0 ? { notificationRoutes } : {},
    // N2K local-action identity (buzzer + PGN 126983/126985). See n2k-identity.ts.
    alertType: identity.alertType,
    alertCategory: identity.alertCategory,
    alertSystem: identity.alertSystem,
    alertSubSystem: identity.alertSubSystem,
    alertNumericId: identity.alertNumericId,
    priority: identity.alertPriority,
    description: def.title
  };
  return rule;
}
var INSTANCE_CHAR = "[A-Za-z0-9_-]";
var INSTANCE = `${INSTANCE_CHAR}{1,24}`;
var RE_BATTERY_VOLTAGE = new RegExp(
  `^electrical\\.batteries\\.${INSTANCE}\\.voltage$`
);
var RE_BATTERY_SOC = new RegExp(
  `^electrical\\.batteries\\.${INSTANCE}\\.capacity\\.stateOfCharge$`
);
var RE_ENGINE_TEMP = new RegExp(`^propulsion\\.${INSTANCE}\\.temperature$`);
var RE_ENGINE_OIL = new RegExp(`^propulsion\\.${INSTANCE}\\.oilPressure$`);
var RE_DEPTH = /^environment\.depth\.belowTransducer$/;
var RE_TANK = new RegExp(
  `^tanks\\.${INSTANCE}\\.${INSTANCE}\\.currentLevel$`
);
var RE_INSIDE_ZONE_TEMP = new RegExp(
  `^environment\\.inside\\.${INSTANCE}\\.temperature$`
);
var RE_SWITCH_CHANNEL = new RegExp(
  `^electrical\\.switches\\.bank\\.${INSTANCE}\\.${INSTANCE}\\.state$`
);
var RE_CAMERA_PERSON = new RegExp(
  `^security\\.cameras\\.${INSTANCE}\\.person$`
);
var RE_CAMERA_AUDIO_ALARM = new RegExp(
  `^security\\.cameras\\.${INSTANCE}\\.audioAlarm$`
);
var RE_CAMERA_CONNECTED = new RegExp(
  `^security\\.cameras\\.${INSTANCE}\\.connected$`
);
var RE_FRIDGE_ZONE = /fridge|freezer|reefer|refriger/i;
var RE_TANK_HIGH_FLUID = /waste|black|grey|gray|holding|sewage/i;
var RE_TANK_FUEL_FLUID = /fuel|diesel|petrol|gasoline/i;
var RE_TANK_WATER_FLUID = /water|fresh|potable/i;
function caseFoldLetters(source) {
  return source.replace(
    /[A-Za-z]/g,
    (ch) => `[${ch.toLowerCase()}${ch.toUpperCase()}]`
  );
}
var FAMILY_FRIDGE_ZONE = {
  source: `^environment\\.inside\\.(?=${INSTANCE}\\.)${INSTANCE_CHAR}*(?:${caseFoldLetters(RE_FRIDGE_ZONE.source)})${INSTANCE_CHAR}*\\.temperature$`,
  flags: ""
};
function tankFluidFamily(fluid, notFluid) {
  const segmentContaining = (re) => `${INSTANCE_CHAR}*(?:${caseFoldLetters(re.source)})${INSTANCE_CHAR}*`;
  const exclude = notFluid === void 0 ? "" : `(?!${segmentContaining(notFluid)}\\.)`;
  return new RegExp(
    `^tanks\\.${exclude}(?=${INSTANCE}\\.)${segmentContaining(fluid)}\\.${INSTANCE}\\.currentLevel$`
  );
}
var RE_TANK_FUEL = tankFluidFamily(RE_TANK_FUEL_FLUID);
var RE_TANK_WATER = tankFluidFamily(RE_TANK_WATER_FLUID, RE_TANK_HIGH_FLUID);
var RE_TANK_WASTE = tankFluidFamily(RE_TANK_HIGH_FLUID);
var PATH_POSITION = "navigation.position";
var PATH_DISTANCE_FROM_ANCHOR = "computed.distanceFromAnchor";
var PATH_ANCHOR_MAX_RADIUS = "navigation.anchor.maxRadius";
var PATH_SHORE_POWER = "electrical.shorePower.connected";
var RE_AC_BUS = /^electrical\.ac\./;
function compileBatteryLow(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const voltsLow = finiteNumber(pm.params, "voltsLow");
  if (voltsLow === void 0) return pending("missing voltsLow threshold");
  const bound = resolvePath(def, catalog, RE_BATTERY_VOLTAGE, /house/i);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no battery voltage path");
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Low",
      trigger: {
        setpoint: voltsLow,
        clearpoint: round2(voltsLow + BATTERY_HYSTERESIS_V)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileBatterySocLow(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const ratio = finiteNumber(pm.params, "lowRatio");
  if (ratio === void 0) return pending("missing lowRatio threshold");
  const bound = resolvePath(def, catalog, RE_BATTERY_SOC);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no battery state of charge");
  const setpoint = round2(ratio * RATIO_TO_PERCENT);
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Low",
      trigger: {
        setpoint,
        clearpoint: round2(setpoint + BATTERY_SOC_HYSTERESIS_PCT)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileEngineOvertemp(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const maxCelsius = finiteNumber(pm.params, "maxCelsius");
  if (maxCelsius === void 0) return pending("missing maxCelsius threshold");
  const bound = resolvePath(def, catalog, RE_ENGINE_TEMP);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no engine temperature path");
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "High",
      trigger: {
        setpoint: maxCelsius,
        clearpoint: round2(maxCelsius - ENGINE_TEMP_HYSTERESIS_C)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileLowOilPressure(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const minKpa = finiteNumber(pm.params, "minKpa");
  if (minKpa === void 0) return pending("missing minKpa threshold");
  const bound = resolvePath(def, catalog, RE_ENGINE_OIL);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no engine oil-pressure path");
  const setpoint = round2(minKpa * KPA_TO_HPA);
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Low",
      trigger: {
        setpoint,
        clearpoint: round2(setpoint * OIL_HYSTERESIS_FACTOR)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileShallowDepth(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const minMeters = finiteNumber(pm.params, "minMeters");
  if (minMeters === void 0) return pending("missing minMeters threshold");
  const bound = resolvePath(def, catalog, RE_DEPTH);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no depth path");
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Low",
      trigger: {
        setpoint: minMeters,
        clearpoint: round2(minMeters * DEPTH_HYSTERESIS_FACTOR)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileDeepDepth(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const maxMeters = finiteNumber(pm.params, "maxMeters");
  if (maxMeters === void 0) return pending("missing maxMeters threshold");
  const bound = resolvePath(def, catalog, RE_DEPTH);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no depth path");
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "High",
      trigger: {
        setpoint: maxMeters,
        clearpoint: round2(maxMeters / DEPTH_HYSTERESIS_FACTOR)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function compileGpsLoss(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const staleMs = finiteNumber(pm.params, "staleMs");
  if (staleMs === void 0) return pending("missing staleMs threshold");
  const bound = resolveFixed(def, catalog, PATH_POSITION);
  const path = bound.path;
  if (path === void 0)
    return pending(
      bound.reason ?? "Needs a GPS receiver that reports the boat position"
    );
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "DataUnavailable",
      trigger: {},
      setDelaySeconds: msToSeconds(staleMs)
    })
  };
}
function compileAnchorDrift(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const radiusMeters = finiteNumber(pm.params, "radiusMeters");
  if (radiusMeters === void 0)
    return pending("missing radiusMeters threshold");
  const bound = resolveFixed(def, catalog, PATH_DISTANCE_FROM_ANCHOR);
  const path = bound.path;
  if (path === void 0)
    return pending(
      bound.reason ?? "Starts watching once the anchor watch is armed"
    );
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "AnchorWatch",
      trigger: {
        triggerRadius: radiusMeters,
        triggerRadiusPath: PATH_ANCHOR_MAX_RADIUS,
        clearRadiusFactor: ANCHOR_CLEAR_FACTOR
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function shorePowerPendingReason(catalog) {
  for (const path of catalog) {
    if (RE_AC_BUS.test(path))
      return "Your boat reports AC power, but nothing that says whether it is shore power or a generator";
  }
  return "Needs a shore-power monitor that reports when the dock supply is connected";
}
function compileShorePowerLost(def, catalog) {
  const bound = resolveFixed(def, catalog, PATH_SHORE_POWER);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? shorePowerPendingReason(catalog));
  const pm = primaryParams(def);
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Off",
      // Arm after normal (core ADR-208, TOM-736): the alarm is for shore power
      // going AWAY. Until the boat has been plugged in this stay it cannot fire,
      // so arriving at a berth and not plugging in yet is not "lost". A core
      // older than the flag ignores it and alarms as before.
      trigger: { armAfterNormal: true },
      setDelaySeconds: pm ? setDelayFrom(pm.params) : 0
    })
  };
}
var BILGE_PIN_REQUIRED_REASON = "Pick the bilge switch channel it should watch";
var BILGE_PUMP_PIN_REQUIRED_REASON = "Pick the bilge pump channel it should watch";
var SMOKE_CO_GAS_PIN_REQUIRED_REASON = "Pick the switch channel your smoke, CO or gas detector is wired to";
var HATCH_SECURITY_PIN_REQUIRED_REASON = "Pick the switch channel your hatch, door or security sensor is wired to";
var CONTACT_ALARM_STATE_KEY = "alarmState";
function isContactStateParam(key) {
  return key === CONTACT_ALARM_STATE_KEY;
}
var CONTACT_ALARM_STATE_CONFLICT = "The alarm's modes disagree on whether the channel reads on or off when it trips";
function recipeTakesContactPolarity(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.params.some(
    (p) => p.key === CONTACT_ALARM_STATE_KEY
  ) === true;
}
function sharedContactAlarmState(thresholdsByMode) {
  const values = /* @__PURE__ */ new Set();
  for (const block of Object.values(thresholdsByMode)) {
    const v = block?.[CONTACT_ALARM_STATE_KEY];
    if (v !== void 0) values.add(v);
  }
  if (values.size > 1) return "conflict";
  return values.values().next().value;
}
var CONTACT_ALARM_STATE_MUST_MATCH = `${CONTACT_ALARM_STATE_KEY} has to be the same in every mode: it says how the channel is wired`;
function normaliseContactAlarmState(recipeId, next, stored) {
  const copy = () => Object.fromEntries(
    Object.entries(next).map(([m, b]) => [m, { ...b }])
  );
  if (!recipeTakesContactPolarity(recipeId)) return { ok: true, value: copy() };
  const given = sharedContactAlarmState(next);
  if (given === "conflict")
    return { ok: false, message: CONTACT_ALARM_STATE_MUST_MATCH };
  const kept = stored === void 0 ? void 0 : sharedContactAlarmState(stored);
  const value = given !== void 0 ? given : typeof kept === "number" ? kept : void 0;
  const out = copy();
  if (value !== void 0) {
    for (const block of Object.values(out))
      block[CONTACT_ALARM_STATE_KEY] = value;
  }
  return { ok: true, value: out };
}
function recipeChannelIdOnly(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.channelIdOnly === true;
}
function isBareChannelIdOnly(recipeId) {
  return recipeChannelIdOnly(recipeId) && parseRecipeId(recipeId).instance === void 0;
}
var CHANNEL_ID_REQUIRED_REASON = "This alarm was made before alarms were kept per channel, so it watches nothing. Create it again on its detector's switch channel, then delete this one";
function withoutContactAlarmState(block) {
  const { [CONTACT_ALARM_STATE_KEY]: _dropped, ...rest } = block;
  return rest;
}
function recipeRevivedWithNewArm(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.revivedWithNewArm === true;
}
var CONTACT_ALARM_STATE_REASON = "Say whether this channel reads on or off when it trips";
function contactArm(pinReason, fixedAlarmState) {
  return (def, catalog) => {
    if (isBareChannelIdOnly(def.recipeId))
      return pending(CHANNEL_ID_REQUIRED_REASON);
    if (def.binding?.path === void 0) return pending(pinReason);
    const bound = resolvePinnedPath(
      def,
      catalog,
      (p) => RE_SWITCH_CHANNEL.test(p)
    );
    if (bound === void 0) return pending(pinReason);
    const path = bound.path;
    if (path === void 0) return pending(bound.reason ?? pinReason);
    const pm = primaryParams(def);
    const shared = fixedAlarmState ?? sharedContactAlarmState(def.thresholdsByMode);
    if (shared === "conflict") return pending(CONTACT_ALARM_STATE_CONFLICT);
    const alarmState = shared;
    if (alarmState !== 0 && alarmState !== 1)
      return pending(CONTACT_ALARM_STATE_REASON);
    return {
      rule: buildRule(def, {
        inputPath: path,
        ruleClass: alarmState === 1 ? "On" : "Off",
        trigger: {},
        setDelaySeconds: pm ? setDelayFrom(pm.params) : 0
      })
    };
  };
}
var MAX_CYCLE_WINDOW_SECONDS = 3600;
function compileBilgePumpCycling(def, catalog) {
  if (def.binding?.path === void 0)
    return pending(BILGE_PUMP_PIN_REQUIRED_REASON);
  const bound = resolvePinnedPath(
    def,
    catalog,
    (p) => RE_SWITCH_CHANNEL.test(p)
  );
  if (bound === void 0) return pending(BILGE_PUMP_PIN_REQUIRED_REASON);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? BILGE_PUMP_PIN_REQUIRED_REASON);
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const windowMs = finiteNumber(pm.params, "cycleWindowMs");
  if (windowMs === void 0) return pending("missing cycleWindowMs threshold");
  const maxCycles = finiteNumber(pm.params, "maxCycles");
  if (maxCycles === void 0) return pending("missing maxCycles threshold");
  const windowSeconds = msToSeconds(windowMs);
  if (windowSeconds < 1 || windowSeconds > MAX_CYCLE_WINDOW_SECONDS)
    return pending(
      "The counting window has to be between 1 second and 60 minutes"
    );
  if (maxCycles < 1)
    return pending("The number of pump starts has to be at least 1");
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "CycleCount",
      trigger: { windowSeconds, maxCycles },
      setDelaySeconds: 0
    })
  };
}
var compileBilgeHigh = contactArm(BILGE_PIN_REQUIRED_REASON, 1);
function compileTankLevel(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const bound = resolvePath(def, catalog, RE_TANK);
  const path = bound.path;
  if (path === void 0)
    return pending(bound.reason ?? "boat reports no tank currentLevel path");
  const fluid = path.split(".")[1] ?? "";
  const setDelaySeconds = setDelayFrom(pm.params);
  if (RE_TANK_HIGH_FLUID.test(fluid)) {
    const highRatio = finiteNumber(pm.params, "highRatio");
    if (highRatio === void 0) return pending("missing highRatio threshold");
    const setpoint2 = round2(highRatio * RATIO_TO_PERCENT);
    return {
      rule: buildRule(def, {
        inputPath: path,
        ruleClass: "High",
        trigger: {
          setpoint: setpoint2,
          clearpoint: round2(setpoint2 - TANK_HYSTERESIS_PCT)
        },
        setDelaySeconds
      })
    };
  }
  const lowRatio = finiteNumber(pm.params, "lowRatio");
  if (lowRatio === void 0) return pending("missing lowRatio threshold");
  const setpoint = round2(lowRatio * RATIO_TO_PERCENT);
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "Low",
      trigger: { setpoint, clearpoint: round2(setpoint + TANK_HYSTERESIS_PCT) },
      setDelaySeconds
    })
  };
}
function tankArm(familyRe, ruleClass, paramKey, noPathReason) {
  return (def, catalog) => {
    const pm = primaryParams(def);
    if (!pm) return pending("no thresholds for any enabled mode");
    const ratio = finiteNumber(pm.params, paramKey);
    if (ratio === void 0) return pending(`missing ${paramKey} threshold`);
    const bound = resolvePath(def, catalog, familyRe);
    const path = bound.path;
    if (path === void 0) return pending(bound.reason ?? noPathReason);
    const setpoint = round2(ratio * RATIO_TO_PERCENT);
    return {
      rule: buildRule(def, {
        inputPath: path,
        ruleClass,
        trigger: {
          setpoint,
          clearpoint: round2(
            ruleClass === "High" ? setpoint - TANK_HYSTERESIS_PCT : setpoint + TANK_HYSTERESIS_PCT
          )
        },
        setDelaySeconds: setDelayFrom(pm.params)
      })
    };
  };
}
function cameraArm(familyRe, ruleClass, noPathReason) {
  return (def, catalog) => {
    const bound = resolvePath(def, catalog, familyRe);
    const path = bound.path;
    if (path === void 0) return pending(bound.reason ?? noPathReason);
    const pm = primaryParams(def);
    return {
      rule: buildRule(def, {
        inputPath: path,
        ruleClass,
        trigger: {},
        setDelaySeconds: pm ? setDelayFrom(pm.params) : 0
      })
    };
  };
}
var CAMERA_NO_PATH_REASON = "Needs a UniFi Protect camera connected to your boat\u2019s network";
function fridgeZoneSegment(path) {
  return path.split(".")[2] ?? "";
}
function compileFridgeHighTemp(def, catalog) {
  const pm = primaryParams(def);
  if (!pm) return pending("no thresholds for any enabled mode");
  const maxCelsius = finiteNumber(pm.params, "maxCelsius");
  if (maxCelsius === void 0) return pending("missing maxCelsius threshold");
  const bound = resolveFridgeZone(def, catalog);
  const path = bound.path;
  if (path === void 0)
    return pending(
      bound.reason ?? "no fridge/freezer temperature zone in catalog"
    );
  return {
    rule: buildRule(def, {
      inputPath: path,
      ruleClass: "High",
      trigger: {
        setpoint: maxCelsius,
        clearpoint: round2(maxCelsius - FRIDGE_HYSTERESIS_C)
      },
      setDelaySeconds: setDelayFrom(pm.params)
    })
  };
}
function resolveFridgeZone(def, catalog) {
  const isFridge = (p) => RE_INSIDE_ZONE_TEMP.test(p) && RE_FRIDGE_ZONE.test(fridgeZoneSegment(p));
  const pinned = resolvePinnedPath(def, catalog, isFridge);
  if (pinned) return pinned;
  for (const raw of def.sourcePaths) {
    const p = stripDeviceSuffix(raw);
    if (catalog.has(p) && isFridge(p)) return { path: p };
  }
  const first = [...catalog].filter(isFridge).sort()[0];
  return first === void 0 ? {} : { path: first };
}
var DEFAULT_CUSTOM_STALE_MS = 3e5;
function compileCustom(def, catalog) {
  const custom = def.custom;
  if (!custom || def.recipeId !== customRecipeIdFor(def.id)) {
    return pending("malformed custom definition");
  }
  const path = custom.path;
  if (!catalog.has(path)) return pending(`boat does not report ${path}`);
  const params = primaryParams(def)?.params ?? {};
  const setDelaySeconds = setDelayFrom(params, "setDelayMs");
  const clearDelaySeconds = setDelayFrom(params, "clearDelayMs");
  switch (custom.triggerClass) {
    case "High":
    case "Low": {
      const setpoint = finiteNumber(params, "setpoint");
      if (setpoint === void 0) return pending("missing set point");
      const clearpoint = finiteNumber(params, "clearpoint") ?? setpoint;
      if (custom.triggerClass === "High" && clearpoint > setpoint) {
        return pending("clear point must be at or below the set point");
      }
      if (custom.triggerClass === "Low" && clearpoint < setpoint) {
        return pending("clear point must be at or above the set point");
      }
      return {
        rule: buildRule(def, {
          inputPath: path,
          ruleClass: custom.triggerClass,
          trigger: { setpoint, clearpoint },
          setDelaySeconds,
          clearDelaySeconds
        })
      };
    }
    case "On":
    case "Off":
      return {
        rule: buildRule(def, {
          inputPath: path,
          ruleClass: custom.triggerClass,
          trigger: {},
          setDelaySeconds,
          clearDelaySeconds
        })
      };
    case "DataUnavailable": {
      const staleMs = finiteNumber(params, "staleMs") ?? DEFAULT_CUSTOM_STALE_MS;
      return {
        rule: buildRule(def, {
          inputPath: path,
          ruleClass: "DataUnavailable",
          trigger: {},
          setDelaySeconds: msToSeconds(staleMs)
        })
      };
    }
    default: {
      const triggerClass = custom.triggerClass;
      return pending(`unsupported trigger class ${triggerClass}`);
    }
  }
}
var DEBOUNCE_PARAM = {
  key: "debounceMs",
  label: "Wait before setting off",
  unit: "ms",
  min: 0,
  max: 36e5,
  step: 1e3,
  perMode: true,
  display: { unit: "seconds", step: 5 }
};
var CONTACT_ALARM_STATE_PARAM = {
  key: CONTACT_ALARM_STATE_KEY,
  label: "When it trips, the channel reads (1 = ON, a normally-open contact; 0 = OFF, a normally-closed one)",
  unit: "",
  min: 0,
  max: 1,
  step: 1,
  perMode: false
};
var CONTACT_PARAMS_SHAPE = z2.object({
  debounceMs: z2.number(),
  // 0 or 1 only: 2, -1 or 0.5 is refused at authoring (400) rather than stored
  // and left pending.
  [CONTACT_ALARM_STATE_KEY]: z2.union([z2.literal(0), z2.literal(1)]).optional()
});
var TANK_HIGH_PARAM = {
  key: "highRatio",
  label: "High level",
  unit: "ratio",
  min: 0,
  max: 1,
  step: 0.05,
  perMode: true,
  display: { unit: "percent", step: 5 }
};
var TANK_LOW_PARAM = {
  key: "lowRatio",
  label: "Low level",
  unit: "ratio",
  min: 0,
  max: 1,
  step: 0.05,
  perMode: true,
  display: { unit: "percent", step: 5 }
};
var STALENESS_DISPLAY = { unit: "seconds" };
function pendingRecipe(spec) {
  const { reason, ...rest } = spec;
  return { ...rest, compile: () => ({ pending: true, reason }) };
}
var SPEC_LIST = [
  // --- bilge ---------------------------------------------------------------
  //
  // TWO of the three arms. `bilge_pump_cycling` gained one with the evaluator's
  // `CycleCount` class (TOM-101, core ADR-183). `bilge_pump_too_long` still
  // needs a run-duration operator ("this input has been ON for 20 minutes"),
  // which v1's classes cannot express, so it keeps its honest pending reason
  // rather than being approximated with a delay.
  {
    recipeId: "bilge_high",
    instanceFamily: "switch",
    requiresPin: true,
    pinRequiredReason: BILGE_PIN_REQUIRED_REASON,
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    title: "Bilge high water",
    description: "Alarm when a bilge high-water float switch trips.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    pathFamily: familyOf(RE_SWITCH_CHANNEL),
    compile: compileBilgeHigh
  },
  pendingRecipe({
    recipeId: "bilge_pump_too_long",
    title: "Bilge pump running too long",
    description: "Alarm when the bilge pump runs continuously past a limit.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "maxRunMs",
        label: "Pump runs non-stop for",
        unit: "ms",
        min: 0,
        max: 36e5,
        step: 1e3,
        perMode: true,
        display: { unit: "seconds" }
      }
    ],
    paramsShape: z2.object({ maxRunMs: z2.number() }),
    withdrawn: true,
    reason: "Needs a bilge pump that reports when it is running"
  }),
  {
    recipeId: "bilge_pump_cycling",
    instanceFamily: "switch",
    requiresPin: true,
    pinRequiredReason: BILGE_PUMP_PIN_REQUIRED_REASON,
    minCoreVersion: MIN_CORE_FOR_CYCLE_COUNT,
    title: "Bilge pump cycling too often",
    description: "Alarm when the bilge pump starts too many times within a window.",
    // `alarm`, not the `warning` it shipped as a pending recipe: Mike's TOM-79
    // decision (2026-10-03). A pump that keeps starting is water coming in, and
    // it has to reach the phone the way bilge high water does.
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "cycleWindowMs",
        label: "Counting window",
        unit: "ms",
        min: 0,
        max: 36e5,
        step: 1e3,
        perMode: true,
        display: { unit: "minutes" }
      },
      {
        // A plain count — no `display`, the stored unit is already the owner's.
        key: "maxCycles",
        label: "Pump starts in that window",
        unit: "count",
        min: 1,
        max: 100,
        step: 1,
        perMode: true
      }
    ],
    paramsShape: z2.object({ cycleWindowMs: z2.number(), maxCycles: z2.number() }),
    pathFamily: familyOf(RE_SWITCH_CHANNEL),
    compile: compileBilgePumpCycling
  },
  // --- shore power ---------------------------------------------------------
  {
    recipeId: "shore_power_lost",
    title: "Shore power lost",
    description: "Warn when the shore-power AC connection drops.",
    defaultSeverity: "warning",
    defaultModes: ["Docked", "Moored"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    pathFamily: fixedFamily(PATH_SHORE_POWER),
    compile: compileShorePowerLost
  },
  // --- batteries -----------------------------------------------------------
  //
  // FOUR entries, two of them superseded. `house_battery_low` /
  // `house_battery_critical` hard-code "House" in the title and, being one
  // recipe each, could describe exactly one bank — so a boat with a house bank,
  // a start bank and a thruster bank got one voltage alarm, named for a bank it
  // might not even be watching (the arm prefers a `house`-ish path and falls
  // back to whatever sorts first). `battery_low` / `battery_critical` are the
  // same alarm without the assumption: same arm, same thresholds, same
  // hysteresis, a title the instance completes ("Battery low — Starter
  // battery").
  //
  // THE DELAYS ARE CRANKING-SAG PROTECTION, not debounce hygiene. Every bank on
  // a boat sags hard while the starter cranks — a healthy 12 V bank can dip
  // under 10 V for a second or two — so a voltage alarm with no delay fires on
  // every engine start. The shipped defaults ride that out: 60 s for `low`,
  // 30 s for `critical` (the balanced values in
  // `core/src/setup/recommendations/library.ts`; a critical bank is worth
  // hearing about sooner, and 30 s is still an order of magnitude longer than a
  // crank). An owner editing these down to zero re-introduces the false alarm,
  // which is why the field reads "Wait before setting off" rather than
  // "debounce".
  {
    recipeId: "house_battery_low",
    instanceFamily: "battery",
    // Superseded by `battery_low` — see `deprecated` on the spec type.
    deprecated: true,
    title: "House battery low",
    description: "Warn when the house battery voltage falls below the floor.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "voltsLow",
        label: "Low voltage",
        unit: "V",
        min: 8,
        max: 60,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ voltsLow: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_VOLTAGE),
    compile: compileBatteryLow
  },
  {
    recipeId: "house_battery_critical",
    instanceFamily: "battery",
    // Superseded by `battery_critical`.
    deprecated: true,
    title: "House battery critical",
    description: "Alarm when the house battery voltage reaches a critical low.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "voltsLow",
        label: "Critical voltage",
        unit: "V",
        min: 8,
        max: 60,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ voltsLow: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_VOLTAGE),
    compile: compileBatteryLow
  },
  {
    recipeId: "battery_low",
    instanceFamily: "battery",
    replaces: ["house_battery_low"],
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    // No "House". One of these is armed PER BANK, and the bank's own name
    // completes the title (`instanceTitle`): "Battery low — Starter battery".
    title: "Battery low",
    description: "Warn when a battery bank's voltage falls below the floor.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "voltsLow",
        label: "Low voltage",
        unit: "V",
        min: 8,
        max: 60,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ voltsLow: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_VOLTAGE),
    // THE SAME ARM as house_battery_low, not a copy: identical thresholds,
    // identical 0.2 V clear band. Its `house`-preferring family walk only
    // decides which bank a BARE id watches, and preferring the house bank is
    // the right guess for an owner who never picked; a per-instance definition
    // pins its bank in the recipe id and never reaches that preference.
    compile: compileBatteryLow
  },
  {
    recipeId: "battery_critical",
    instanceFamily: "battery",
    replaces: ["house_battery_critical"],
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    title: "Battery critical",
    description: "Alarm when a battery bank's voltage reaches a critical low.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "voltsLow",
        label: "Critical voltage",
        unit: "V",
        min: 8,
        max: 60,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ voltsLow: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_VOLTAGE),
    compile: compileBatteryLow
  },
  // State of charge, for a LiFePO4 bank (TOM-318, core ADR-197). A LiFePO4
  // bank holds about 3.2 to 3.3 V per cell from about 90 % down to about 20 %,
  // so its voltage says little about how much charge is left until the bank is
  // nearly empty. Core offers these two only for a bank it knows is lithium and
  // that reports its state of charge. They do not replace the voltage pair,
  // which stays armed at LiFePO4 voltages as a backstop.
  {
    recipeId: "battery_soc_low",
    instanceFamily: "battery",
    minCoreVersion: MIN_CORE_FOR_BATTERY_SOC,
    title: "Battery charge low",
    description: "Warn when a battery bank's state of charge falls below the floor.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "lowRatio",
        label: "Low charge",
        unit: "ratio",
        min: 0,
        max: 1,
        step: 0.05,
        perMode: true,
        display: { unit: "percent", step: 5 }
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ lowRatio: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_SOC),
    compile: compileBatterySocLow
  },
  {
    recipeId: "battery_soc_critical",
    instanceFamily: "battery",
    minCoreVersion: MIN_CORE_FOR_BATTERY_SOC,
    title: "Battery charge critical",
    description: "Alarm when a battery bank's state of charge reaches a critical low.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "lowRatio",
        label: "Critical charge",
        unit: "ratio",
        min: 0,
        max: 1,
        step: 0.05,
        perMode: true,
        display: { unit: "percent", step: 5 }
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ lowRatio: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_BATTERY_SOC),
    compile: compileBatterySocLow
  },
  // --- charging (RETIRED — see the reason below) ---------------------------
  //
  // Withdrawn by `alarm_binding_gap`. Its stated reason was not true: the
  // measured vessel's charger reports its state perfectly well
  // (`electrical.chargers.<n>.chargingMode` cycles bulk / absorption /
  // inverting / power assist). What no boat reports is that the charger has
  // FAILED, and "not charging" is not failure — a healthy charger sits in
  // `inverting` for most of a night at anchor.
  //
  // So there was no honest binding to reach for, only a promise on the owner's
  // alarm list that nothing could keep. `deprecated` withdraws it from every
  // offer lane; the spec stays so already-provisioned rows resolve to this
  // entry instead of degrading to `unknown recipeId`, and the boat's boot sweep
  // retires them. A real charger-health alarm is a new recipe with its own
  // definition of failure, not a rescue of this one.
  pendingRecipe({
    recipeId: "charger_alternator_failure",
    title: "Charger or alternator failure",
    description: "Warn when the charger/alternator stops while it should run.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Maintenance"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    withdrawn: true,
    reason: "Neomaris can\u2019t tell a failed charger from one that is simply not charging yet"
  }),
  // --- engine --------------------------------------------------------------
  {
    recipeId: "engine_overtemp",
    instanceFamily: "engine",
    title: "Engine overtemperature",
    description: "Alarm above the engine over-temperature limit.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Maintenance"],
    params: [
      {
        key: "maxCelsius",
        label: "Max temperature",
        unit: "\xB0C",
        min: 0,
        max: 200,
        step: 1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ maxCelsius: z2.number(), debounceMs: z2.number() }),
    supportsCondition: {
      preset: "engine_running",
      defaultMinRpm: DEFAULT_ENGINE_RUNNING_RPM
    },
    pathFamily: familyOf(RE_ENGINE_TEMP),
    compile: compileEngineOvertemp
  },
  {
    recipeId: "low_oil_pressure",
    instanceFamily: "engine",
    title: "Low engine oil pressure",
    description: "Emergency alarm below the minimum oil pressure while running.",
    defaultSeverity: "emergency",
    defaultModes: ["Underway", "Maintenance"],
    params: [
      {
        key: "minKpa",
        label: "Minimum pressure",
        unit: "kPa",
        min: 0,
        max: 1e3,
        step: 5,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ minKpa: z2.number(), debounceMs: z2.number() }),
    supportsCondition: {
      preset: "engine_running",
      defaultMinRpm: DEFAULT_ENGINE_RUNNING_RPM
    },
    pathFamily: familyOf(RE_ENGINE_OIL),
    compile: compileLowOilPressure
  },
  // --- navigation ----------------------------------------------------------
  {
    recipeId: "gps_loss",
    title: "GPS position lost",
    description: "Warn when the position fix goes stale or unavailable.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "staleMs",
        label: "Stops reporting for",
        unit: "ms",
        min: 0,
        max: 6e5,
        step: 1e3,
        perMode: true,
        display: STALENESS_DISPLAY
      }
    ],
    paramsShape: z2.object({ staleMs: z2.number() }),
    pathFamily: fixedFamily(PATH_POSITION),
    compile: compileGpsLoss
  },
  // --- gps source mismatch (pending: needs a source-compare op) ------------
  pendingRecipe({
    recipeId: "gps_source_mismatch",
    title: "GPS source mismatch",
    description: "Caution when the selected GPS disagrees with another source.",
    defaultSeverity: "caution",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "disagreementMeters",
        label: "Disagreement",
        unit: "m",
        min: 0,
        max: 1e4,
        step: 10,
        perMode: true
      }
    ],
    paramsShape: z2.object({ disagreementMeters: z2.number() }),
    withdrawn: true,
    reason: "Neomaris can\u2019t compare two GPS receivers against each other yet"
  }),
  {
    recipeId: "anchor_drift",
    title: "Anchor drift",
    description: "Alarm when the vessel drifts past the anchor watch radius.",
    defaultSeverity: "alarm",
    defaultModes: ["Anchored"],
    params: [
      {
        key: "radiusMeters",
        label: "Watch radius",
        unit: "m",
        min: 5,
        max: 500,
        step: 1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ radiusMeters: z2.number(), debounceMs: z2.number() }),
    pathFamily: fixedFamily(PATH_DISTANCE_FROM_ANCHOR),
    compile: compileAnchorDrift
  },
  {
    recipeId: "shallow_depth",
    title: "Shallow water",
    description: "Alarm when depth falls below the safety margin.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored"],
    params: [
      {
        key: "minMeters",
        label: "Minimum depth",
        unit: "m",
        min: 0,
        max: 100,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ minMeters: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_DEPTH),
    compile: compileShallowDepth
  },
  // --- depth at anchor -----------------------------------------------------
  //
  // The same sounder path as `shallow_depth`, watched from BOTH sides and only
  // while `Anchored`. They are separate recipes rather than extra params on the
  // underway alarm because they answer a different question: `shallow_depth`
  // asks "is there water under the keel right now", these two ask "will this
  // anchorage still work at the bottom of the tide, and is it deeper than the
  // rode can hold". The numbers therefore move with the tide forecast rather
  // than being set once by an owner, which is what the anchor assistant manages
  // at anchor-set.
  //
  // ONE PER BOAT, like `shallow_depth`: the family is the single depth path, so
  // neither carries an `instanceFamily`.
  {
    recipeId: "anchor_depth_low",
    minCoreVersion: MIN_CORE_FOR_ANCHOR_DEPTH,
    title: "Shallow water at anchor",
    description: "Alarm when the depth at anchor falls below the safe minimum.",
    defaultSeverity: "alarm",
    defaultModes: ["Anchored"],
    params: [
      {
        key: "minMeters",
        label: "Minimum depth",
        unit: "m",
        min: 0,
        max: 100,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ minMeters: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_DEPTH),
    // THE SAME ARM as shallow_depth, not a copy: same Low class, same ×1.1
    // clear band, same param key. Only the mode set and who owns the number
    // differ, and neither reaches the compiler.
    compile: compileShallowDepth
  },
  {
    recipeId: "deep_depth",
    minCoreVersion: MIN_CORE_FOR_ANCHOR_DEPTH,
    title: "Deep water at anchor",
    description: "Alarm when the depth at anchor rises above the rode's limit.",
    defaultSeverity: "alarm",
    defaultModes: ["Anchored"],
    params: [
      {
        // Floored at 3 m rather than 0: an anchorage shallower than that is
        // what `anchor_depth_low` is for, and a deep alarm set there would
        // fire on the tide coming in.
        key: "maxMeters",
        label: "Maximum depth",
        unit: "m",
        min: 3,
        max: 200,
        step: 0.1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ maxMeters: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_DEPTH),
    compile: compileDeepDepth
  },
  // --- tanks ---------------------------------------------------------------
  //
  // FOUR entries, one of them superseded. `tank_high` is a single recipe that
  // tried to be every tank alarm on the boat: one definition, both a `highRatio`
  // and a `lowRatio`, and the direction chosen at compile time by sniffing the
  // bound path's fluid. On a boat with fuel, fresh water and a holding tank that
  // is one alarm for three tanks — the owner cannot say which tank it watches,
  // cannot arm the other two, and reads a form asking for two numbers of which
  // exactly one is used. It is the #1 complaint about the alarm panel.
  //
  // The three replacements are one direction, one fluid class and one number
  // each, so the recipe already knows which way it fires before it meets a path,
  // and one alarm per tank is just three definitions with different pins.
  {
    recipeId: "tank_high",
    instanceFamily: "tank",
    // Superseded by tank_fuel_low / tank_water_low / tank_waste_high.
    deprecated: true,
    title: "Tank high / low",
    description: "Warn near full (waste/black) or near empty (fuel/fresh).",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    // BOTH directions, which is the shape of the problem: one of these two
    // numbers is dead weight on any given tank, and which one depends on a fluid
    // segment the owner never sees.
    params: [TANK_HIGH_PARAM, TANK_LOW_PARAM, DEBOUNCE_PARAM],
    paramsShape: z2.object({
      highRatio: z2.number(),
      lowRatio: z2.number(),
      debounceMs: z2.number()
    }),
    pathFamily: familyOf(RE_TANK),
    compile: compileTankLevel
  },
  {
    recipeId: "tank_fuel_low",
    instanceFamily: "tank",
    replaces: ["tank_high"],
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    title: "Fuel tank low",
    description: "Warn when a fuel tank is running low.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [TANK_LOW_PARAM, DEBOUNCE_PARAM],
    paramsShape: z2.object({ lowRatio: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_TANK_FUEL),
    compile: tankArm(
      RE_TANK_FUEL,
      "Low",
      "lowRatio",
      "boat reports no fuel tank level"
    )
  },
  {
    recipeId: "tank_water_low",
    instanceFamily: "tank",
    replaces: ["tank_high"],
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    title: "Water tank low",
    description: "Warn when a fresh-water tank is running low.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [TANK_LOW_PARAM, DEBOUNCE_PARAM],
    paramsShape: z2.object({ lowRatio: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_TANK_WATER),
    compile: tankArm(
      RE_TANK_WATER,
      "Low",
      "lowRatio",
      "boat reports no fresh-water tank level"
    )
  },
  {
    recipeId: "tank_waste_high",
    instanceFamily: "tank",
    replaces: ["tank_high"],
    minCoreVersion: MIN_CORE_FOR_SPLIT_RECIPES,
    title: "Waste tank nearly full",
    description: "Warn when a waste or holding tank is nearly full.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [TANK_HIGH_PARAM, DEBOUNCE_PARAM],
    paramsShape: z2.object({ highRatio: z2.number(), debounceMs: z2.number() }),
    pathFamily: familyOf(RE_TANK_WASTE),
    compile: tankArm(
      RE_TANK_WASTE,
      "High",
      "highRatio",
      "boat reports no waste tank level"
    )
  },
  // --- fuel / fire / gas ---------------------------------------------------
  pendingRecipe({
    recipeId: "water_in_fuel",
    title: "Water in fuel",
    description: "Alarm when water is detected in the fuel/water separator.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    withdrawn: true,
    reason: "Needs a water-in-fuel sensor on the fuel/water separator"
  }),
  // A detector's alarm contact on a switch channel (TOM-136, core ADR-217).
  // Withdrawn by ADR-216 while nothing could arm it; the switch-channel arm is
  // `bilge_high`'s, with a polarity param for a normally-closed contact.
  {
    recipeId: "smoke_co_gas",
    instanceFamily: "switch",
    requiresPin: true,
    pinRequiredReason: SMOKE_CO_GAS_PIN_REQUIRED_REASON,
    minCoreVersion: MIN_CORE_FOR_DETECTOR_CONTACTS,
    revivedWithNewArm: true,
    channelIdOnly: true,
    title: "Smoke, CO, or gas detected",
    description: "Emergency alarm when a smoke, CO or gas detector's alarm contact trips.",
    defaultSeverity: "emergency",
    // Every mode: fire and gas do not wait for the boat to be under way.
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [DEBOUNCE_PARAM, CONTACT_ALARM_STATE_PARAM],
    paramsShape: CONTACT_PARAMS_SHAPE,
    pathFamily: familyOf(RE_SWITCH_CHANNEL),
    compile: contactArm(SMOKE_CO_GAS_PIN_REQUIRED_REASON)
  },
  // --- security --------------------------------------------------------------
  {
    recipeId: "hatch_security",
    instanceFamily: "switch",
    requiresPin: true,
    pinRequiredReason: HATCH_SECURITY_PIN_REQUIRED_REASON,
    minCoreVersion: MIN_CORE_FOR_DETECTOR_CONTACTS,
    revivedWithNewArm: true,
    channelIdOnly: true,
    title: "Hatch / door / security sensor active",
    description: "Alarm when a hatch, door or security sensor trips while the boat is left.",
    defaultSeverity: "alarm",
    // Armed while the crew is away from the helm: at the dock, on a mooring, at
    // anchor. Under way and in maintenance, people open hatches.
    defaultModes: ["Docked", "Moored", "Anchored"],
    params: [DEBOUNCE_PARAM, CONTACT_ALARM_STATE_PARAM],
    paramsShape: CONTACT_PARAMS_SHAPE,
    pathFamily: familyOf(RE_SWITCH_CHANNEL),
    compile: contactArm(HATCH_SECURITY_PIN_REQUIRED_REASON)
  },
  // --- cameras (TOM-824, core ADR-209) ------------------------------------
  {
    recipeId: "camera_person",
    instanceFamily: "camera",
    minCoreVersion: MIN_CORE_FOR_CAMERAS,
    title: "Person seen",
    description: "Alarm when a camera sees a person while the boat is left at anchor, on a mooring or at the dock.",
    defaultSeverity: "alarm",
    defaultModes: ["Anchored", "Moored", "Docked"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    pathFamily: familyOf(RE_CAMERA_PERSON),
    compile: cameraArm(RE_CAMERA_PERSON, "On", CAMERA_NO_PATH_REASON)
  },
  {
    recipeId: "camera_audio_alarm",
    instanceFamily: "camera",
    minCoreVersion: MIN_CORE_FOR_CAMERAS,
    title: "Alarm sound heard",
    description: "Alarm when a camera hears a smoke alarm, CO alarm, siren or breaking glass.",
    defaultSeverity: "alarm",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    pathFamily: familyOf(RE_CAMERA_AUDIO_ALARM),
    compile: cameraArm(RE_CAMERA_AUDIO_ALARM, "On", CAMERA_NO_PATH_REASON)
  },
  {
    recipeId: "camera_offline",
    instanceFamily: "camera",
    minCoreVersion: MIN_CORE_FOR_CAMERAS,
    title: "Camera offline",
    description: "Warn when a camera stays disconnected from UniFi Protect.",
    // A warning, not a caution (Mike, 2026-10-20): the cloud pushes from
    // `warning` up by default, and an owner should hear a camera go dark.
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [DEBOUNCE_PARAM],
    paramsShape: z2.object({ debounceMs: z2.number() }),
    pathFamily: familyOf(RE_CAMERA_CONNECTED),
    compile: cameraArm(RE_CAMERA_CONNECTED, "Off", CAMERA_NO_PATH_REASON)
  },
  // --- refrigeration -------------------------------------------------------
  {
    recipeId: "fridge_freezer_high_temp",
    instanceFamily: "zone",
    title: "Fridge / freezer high temperature",
    description: "Caution when a fridge/freezer zone stays above the limit.",
    defaultSeverity: "caution",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "maxCelsius",
        label: "Max temperature",
        unit: "\xB0C",
        min: -40,
        max: 40,
        step: 1,
        perMode: true
      },
      DEBOUNCE_PARAM
    ],
    paramsShape: z2.object({ maxCelsius: z2.number(), debounceMs: z2.number() }),
    pathFamily: FAMILY_FRIDGE_ZONE,
    compile: compileFridgeHighTemp
  },
  // --- infrastructure (pending: needs first-class source-health signal) ----
  pendingRecipe({
    recipeId: "gateway_offline",
    title: "NMEA 2000 gateway offline",
    description: "Warn when the gateway stops forwarding bus data.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "offlineMs",
        label: "Out of touch for",
        unit: "ms",
        min: 0,
        max: 6e5,
        step: 1e3,
        perMode: true,
        display: STALENESS_DISPLAY
      }
    ],
    paramsShape: z2.object({ offlineMs: z2.number() }),
    withdrawn: true,
    reason: "Neomaris doesn\u2019t watch gateway health yet"
  }),
  pendingRecipe({
    recipeId: "sensor_stale",
    title: "Important sensor stale or offline",
    description: "Warn when an important sensor goes stale or drops out.",
    defaultSeverity: "warning",
    defaultModes: ["Underway", "Anchored", "Moored", "Docked", "Maintenance"],
    params: [
      {
        key: "staleMs",
        label: "Stops reporting for",
        unit: "ms",
        min: 0,
        max: 6e5,
        step: 1e3,
        perMode: true,
        display: STALENESS_DISPLAY
      }
    ],
    paramsShape: z2.object({ staleMs: z2.number() }),
    withdrawn: true,
    reason: "Neomaris doesn\u2019t watch sensor health yet"
  })
];
var RECIPE_SPECS = Object.freeze(Object.fromEntries(SPEC_LIST.map((s) => [s.recipeId, s])));
var RECIPE_IDS = SPEC_LIST.map((s) => s.recipeId);
function recipeConditionSupport(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.supportsCondition;
}
function defaultConditionsForRecipe(recipeId) {
  const support = recipeConditionSupport(recipeId);
  if (!support) return void 0;
  return {
    logic: "all",
    clauses: [{ preset: support.preset, minRpm: support.defaultMinRpm }]
  };
}
function recipePathFamily(recipeId) {
  return RECIPE_SPECS[recipeId]?.pathFamily;
}
var COMPILABLE_RECIPE_IDS = [
  "bilge_high",
  "bilge_pump_cycling",
  "shore_power_lost",
  "house_battery_low",
  "house_battery_critical",
  "battery_low",
  "battery_critical",
  "battery_soc_low",
  "battery_soc_critical",
  "engine_overtemp",
  "low_oil_pressure",
  "gps_loss",
  "anchor_drift",
  "shallow_depth",
  "anchor_depth_low",
  "deep_depth",
  "tank_high",
  "tank_fuel_low",
  "tank_water_low",
  "tank_waste_high",
  "fridge_freezer_high_temp",
  "smoke_co_gas",
  "hatch_security",
  "camera_person",
  "camera_audio_alarm",
  "camera_offline"
];
var DEPRECATED_RECIPE_IDS = SPEC_LIST.filter(
  (s) => s.deprecated === true
).map((s) => s.recipeId);
function isDeprecatedRecipe(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.deprecated === true;
}
var WITHDRAWN_RECIPE_IDS = SPEC_LIST.filter(
  (s) => s.withdrawn === true
).map((s) => s.recipeId);
function isWithdrawnRecipe(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.withdrawn === true;
}
var RECIPE_WITHDRAWN_ERROR = "recipe_withdrawn";
function withdrawnRecipeMessage(recipeId) {
  const base = parseRecipeId(recipeId).base;
  const title = RECIPE_SPECS[base]?.title ?? base;
  return `"${title}" is no longer offered: no boat has a signal it could watch, so it could never go off. It has been withdrawn.`;
}
function withdrawnRefusal(recipeId) {
  if (!isWithdrawnRecipe(recipeId)) return null;
  const base = parseRecipeId(recipeId).base;
  return {
    error: RECIPE_WITHDRAWN_ERROR,
    message: withdrawnRecipeMessage(base),
    details: { recipeId: base }
  };
}
function withdrawnEditRefusal(postPatchRecipeId, postPatchEnabled) {
  return postPatchEnabled ? withdrawnRefusal(postPatchRecipeId) : null;
}
var CREATE_ON_CHANNEL_ERROR = "create_on_channel";
function bareChannelIdRefusal(recipeId, write) {
  if (!isBareChannelIdOnly(recipeId)) return null;
  if (!write.enabled && !write.pinned) return null;
  const base = parseRecipeId(recipeId).base;
  const title = RECIPE_SPECS[base]?.title ?? base;
  return {
    error: CREATE_ON_CHANNEL_ERROR,
    message: `${title} alarms are kept one per switch channel. Create this alarm on its detector's channel (send binding.path), then delete this one.`,
    details: { recipeId: base }
  };
}
function recipeMinCoreVersion(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.minCoreVersion;
}
function recipeInstanceFamily(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.instanceFamily;
}
var FAN_OUT_RECIPE_IDS = SPEC_LIST.filter(
  (s) => s.instanceFamily !== void 0 && s.deprecated !== true && s.withdrawn !== true
).map((s) => s.recipeId);
function recipeNominalVoltageKeys(recipeId) {
  const spec = RECIPE_SPECS[parseRecipeId(recipeId).base];
  if (spec === void 0 || spec.instanceFamily !== "battery") return [];
  return spec.params.filter((p) => p.unit === "V").map((p) => p.key);
}
var PIN_REQUIRED_RECIPE_IDS = SPEC_LIST.filter(
  (s) => s.requiresPin === true
).map((s) => s.recipeId);
function recipeRequiresPin(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.requiresPin === true;
}
function recipePinRequiredReason(recipeId) {
  const spec = RECIPE_SPECS[parseRecipeId(recipeId).base];
  return spec?.requiresPin === true ? spec.pinRequiredReason : void 0;
}
function recipeDefaultSeverity(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.defaultSeverity;
}
function recipeReplaces(recipeId) {
  return RECIPE_SPECS[parseRecipeId(recipeId).base]?.replaces ?? [];
}
var STORE_UNIT_PLACEHOLDER = "store";
var CUSTOM_SETPOINT_PARAM = {
  key: "setpoint",
  label: "Set point",
  unit: STORE_UNIT_PLACEHOLDER,
  perMode: false
};
var CUSTOM_CLEARPOINT_PARAM = {
  key: "clearpoint",
  label: "Clear point",
  unit: STORE_UNIT_PLACEHOLDER,
  perMode: false
};
var CUSTOM_SET_DELAY_PARAM = {
  key: "setDelayMs",
  label: "Wait before setting off",
  unit: "ms",
  min: 0,
  max: 36e5,
  step: 1e3,
  perMode: false,
  display: { unit: "seconds" }
};
var CUSTOM_CLEAR_DELAY_PARAM = {
  key: "clearDelayMs",
  label: "Wait before clearing",
  unit: "ms",
  min: 0,
  max: 36e5,
  step: 1e3,
  perMode: false,
  display: { unit: "seconds" }
};
var CUSTOM_STALE_PARAM = {
  key: "staleMs",
  label: "Stops reporting for",
  unit: "ms",
  min: 0,
  max: 6e5,
  step: 1e3,
  perMode: false,
  // The SAME unit the recipe staleness windows use — see {@link
  // STALENESS_DISPLAY}. These two carry the identical legend, so they must
  // carry the identical unit.
  display: STALENESS_DISPLAY
};
var CUSTOM_TRIGGER_SPECS = [
  {
    triggerClass: "High",
    params: [
      CUSTOM_SETPOINT_PARAM,
      CUSTOM_CLEARPOINT_PARAM,
      CUSTOM_SET_DELAY_PARAM,
      CUSTOM_CLEAR_DELAY_PARAM
    ]
  },
  {
    triggerClass: "Low",
    params: [
      CUSTOM_SETPOINT_PARAM,
      CUSTOM_CLEARPOINT_PARAM,
      CUSTOM_SET_DELAY_PARAM,
      CUSTOM_CLEAR_DELAY_PARAM
    ]
  },
  {
    triggerClass: "On",
    params: [CUSTOM_SET_DELAY_PARAM, CUSTOM_CLEAR_DELAY_PARAM]
  },
  {
    triggerClass: "Off",
    params: [CUSTOM_SET_DELAY_PARAM, CUSTOM_CLEAR_DELAY_PARAM]
  },
  {
    triggerClass: "DataUnavailable",
    params: [CUSTOM_STALE_PARAM]
  }
];

// src/compile.ts
var COMPILER_OUTPUT_VERSION = 6;
function engineRevolutionsPath(inputPath) {
  const parts = inputPath.split(".");
  if (parts.length < 3 || parts[0] !== "propulsion") return void 0;
  return `${parts[0]}.${parts[1]}.revolutions`;
}
function resolveCondition(def, inputPath, catalog, spec) {
  const conditions = def.conditions;
  if (!conditions) return {};
  const op = conditions.logic;
  const resolved = [];
  let unboundReason;
  for (const clause of conditions.clauses) {
    if ("preset" in clause) {
      if (!spec.supportsCondition) {
        return {
          pending: `condition preset not supported for ${def.recipeId}`
        };
      }
      const revPath = engineRevolutionsPath(inputPath);
      if (revPath !== void 0 && catalog.has(revPath)) {
        resolved.push({
          type: "High",
          path: revPath,
          value: clause.minRpm ?? spec.supportsCondition.defaultMinRpm ?? DEFAULT_ENGINE_RUNNING_RPM
        });
      } else {
        unboundReason = revPath !== void 0 ? `engine RPM not reported (${revPath}); engine-running condition dropped` : `engine RPM path unresolvable from ${inputPath}; engine-running condition dropped`;
      }
    } else {
      if (!catalog.has(clause.path)) {
        return { pending: `condition path not reported: ${clause.path}` };
      }
      resolved.push(clause);
    }
  }
  const out = {};
  if (unboundReason !== void 0) {
    out.conditionUnbound = { reason: unboundReason };
    if (op === "any" || resolved.length === 0) return out;
  }
  if (resolved.length > 0) out.condition = { op, clauses: resolved };
  return out;
}
function applyImplicitPin(def, instance) {
  if (instance === void 0) return def;
  if (def.binding?.path !== void 0) return def;
  return { ...def, binding: { path: instance } };
}
function compileDefinitions(defs, catalog, _opts = {}) {
  const ordered = [...defs].sort(
    (a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  );
  const rules = [];
  const pending2 = [];
  const conditionUnbound = [];
  for (const raw of ordered) {
    if (!raw.enabled) continue;
    const custom = isCustomDef(raw);
    const parsedRecipeId = parseRecipeId(raw.recipeId);
    const spec = custom ? void 0 : RECIPE_SPECS[parsedRecipeId.base];
    if (!custom && spec === void 0) {
      pending2.push({
        defId: raw.id,
        recipeId: raw.recipeId,
        reason: "unknown recipeId"
      });
      continue;
    }
    const { def } = foldDefinitionModes(raw);
    if (def.enabledModes.length === 0) {
      pending2.push({
        defId: def.id,
        recipeId: def.recipeId,
        reason: raw.enabledModes.length > 0 ? `no runtime modes after fold (${raw.enabledModes.join(", ")})` : "no enabled modes"
      });
      continue;
    }
    const pinned = applyImplicitPin(def, parsedRecipeId.instance);
    const result = spec !== void 0 ? spec.compile(pinned, catalog) : compileCustom(pinned, catalog);
    if ("pending" in result) {
      pending2.push({
        defId: def.id,
        recipeId: def.recipeId,
        reason: result.reason
      });
      continue;
    }
    let rule = result.rule;
    if (def.conditions) {
      const resolved = resolveCondition(
        def,
        rule.inputPath,
        catalog,
        spec ?? {}
      );
      if (resolved.pending !== void 0) {
        pending2.push({
          defId: def.id,
          recipeId: def.recipeId,
          reason: resolved.pending
        });
        continue;
      }
      if (resolved.condition !== void 0) {
        rule = { ...rule, condition: resolved.condition };
      }
      if (resolved.conditionUnbound !== void 0) {
        conditionUnbound.push({
          defId: def.id,
          recipeId: def.recipeId,
          reason: resolved.conditionUnbound.reason
        });
      }
    }
    rules.push(rule);
  }
  return { rules, pending: pending2, conditionUnbound, rulesDigest: rulesDigest(rules) };
}

// src/as-custom.ts
function customClassOf(ruleClass) {
  const parsed = customTriggerClassSchema.safeParse(ruleClass);
  return parsed.success ? parsed.data : void 0;
}
function triggerNumber(trigger, key) {
  const raw = trigger[key];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : void 0;
}
function toMs(seconds) {
  return Math.round(seconds * 1e3);
}
function recipeAsCustom(def, catalog) {
  if (def.custom !== void 0 && def.custom !== null) {
    return { ok: false, reason: "definition is already a custom alarm" };
  }
  const compiled = compileDefinitions([{ ...def, enabled: true }], catalog);
  const rule = compiled.rules[0];
  if (rule === void 0) {
    const reason = compiled.pending[0]?.reason ?? "definition does not compile";
    return { ok: false, reason };
  }
  const triggerClass = customClassOf(rule.class);
  if (triggerClass === void 0) {
    return {
      ok: false,
      reason: `rule class ${rule.class} has no custom equivalent`
    };
  }
  const trigger = rule.trigger;
  if (trigger.armAfterNormal === true) {
    return {
      ok: false,
      reason: "it waits for the signal to be healthy before it can fire, which a custom alarm can\u2019t express"
    };
  }
  const thresholds = {};
  if (triggerClass === "High" || triggerClass === "Low") {
    const setpoint = triggerNumber(trigger, "setpoint");
    if (setpoint === void 0) {
      return { ok: false, reason: "compiled rule carries no set point" };
    }
    thresholds.setpoint = setpoint;
    const clearpoint = triggerNumber(trigger, "clearpoint");
    if (clearpoint !== void 0 && clearpoint !== setpoint) {
      thresholds.clearpoint = clearpoint;
    }
  }
  if (triggerClass === "DataUnavailable") {
    thresholds.staleMs = toMs(rule.setDelaySeconds);
  } else {
    if (rule.setDelaySeconds > 0)
      thresholds.setDelayMs = toMs(rule.setDelaySeconds);
    if (rule.clearDelaySeconds > 0) {
      thresholds.clearDelayMs = toMs(rule.clearDelaySeconds);
    }
  }
  const resolved = conditionsFromCompiled(rule.condition);
  return {
    ok: true,
    value: {
      custom: { v: 1, path: rule.inputPath, triggerClass },
      thresholds,
      ...resolved !== void 0 ? { conditions: resolved } : {}
    }
  };
}
function conditionsFromCompiled(condition) {
  if (condition === null || typeof condition !== "object") return void 0;
  const { op, clauses } = condition;
  if (!Array.isArray(clauses) || clauses.length === 0) return void 0;
  const parsed = alertConditionsSchema.safeParse({
    logic: op === "any" ? "any" : "all",
    clauses
  });
  return parsed.success ? parsed.data : void 0;
}

// src/instance-names.ts
var SWITCH_CHANNEL_SEGMENTS = 6;
var UNIDENTIFIED_TANK_LABEL = "Unidentified tank";
function isUnreadable(seg) {
  return seg === "undefined";
}
var INSTANCE_TITLE_SEPARATOR = " \u2014 ";
var MAX_INSTANCE_TITLE_LEN = 200;
function humanizeCamel(seg) {
  return seg.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
}
function capitalizeFirst(s) {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
function titleize(seg) {
  return capitalizeFirst(humanizeCamel(seg));
}
function isNumericSegment(seg) {
  return /^\d+$/.test(seg);
}
var ORDINAL_INSTANCE_LIMIT = 100;
function displayInstance(seg) {
  const n = Number(seg);
  return String(n < ORDINAL_INSTANCE_LIMIT ? n + 1 : n);
}
function instanceNoun(seg, numeric, named) {
  return isNumericSegment(seg) ? numeric(displayInstance(seg)) : named(seg);
}
function instanceLabel(family, path, opts = {}) {
  const named = opts.displayName?.trim();
  if (named !== void 0 && named.length > 0) return named;
  const seg = path.split(".").filter((s) => s.length > 0);
  switch (family) {
    // tanks.<fluid>.<instance>.<measurement…>
    case "tank": {
      if (seg.length < 4 || seg[0] !== "tanks") return void 0;
      const fluid = seg[1];
      const instance = seg[2];
      if (isUnreadable(fluid) && isUnreadable(instance)) {
        return UNIDENTIFIED_TANK_LABEL;
      }
      if (isUnreadable(fluid)) {
        return `${UNIDENTIFIED_TANK_LABEL} ${instanceNoun(instance, (n) => n, titleize)}`;
      }
      if (isUnreadable(instance)) {
        return `Unidentified ${humanizeCamel(fluid)} tank`;
      }
      return `${titleize(fluid)} tank ${instanceNoun(
        instance,
        (n) => n,
        titleize
      )}`;
    }
    // propulsion.<instance>.<measurement…>
    case "engine": {
      if (seg.length < 3 || seg[0] !== "propulsion") return void 0;
      if (isUnreadable(seg[1])) return "Unidentified engine";
      return instanceNoun(
        seg[1],
        (n) => `Engine ${n}`,
        (s) => `${titleize(s)} engine`
      );
    }
    // propulsion.<instance>.transmission.<measurement…> — a gearbox. PGN 127493
    // carries its own instance byte, and which engine a gearbox belongs to is
    // the boat owner's answer (cloud ADR 0312 §3), so the name says none:
    // "Port gearbox", not "Port engine gearbox".
    case "gearbox": {
      if (seg.length < 4 || seg[0] !== "propulsion" || seg[2] !== "transmission") {
        return void 0;
      }
      if (isUnreadable(seg[1])) return "Unidentified gearbox";
      return instanceNoun(
        seg[1],
        (n) => `Gearbox ${n}`,
        (s) => `${titleize(s)} gearbox`
      );
    }
    // electrical.batteries.<instance>.<measurement…>
    case "battery": {
      if (seg.length < 4 || seg[0] !== "electrical" || seg[1] !== "batteries") {
        return void 0;
      }
      if (isUnreadable(seg[2])) return "Unidentified battery";
      return instanceNoun(
        seg[2],
        (n) => `Battery ${n}`,
        (s) => `${titleize(s)} battery`
      );
    }
    // environment.inside.<zone>.<measurement…> — the zone IS the name
    // ("Galley fridge"), with no noun to append: a fridge zone is already one.
    case "zone": {
      if (seg.length < 4 || seg[0] !== "environment" || seg[1] !== "inside") {
        return void 0;
      }
      return titleize(seg[2]);
    }
    // electrical.switches.bank.<bank>.<channel>.state — a dry-contact channel.
    //
    // THE CHANNEL NUMBER IS NOT +1'd AND THE BANK IS. PGN 127501 numbers
    // channels from 1 already, while the bank rides the ordinary 0-based data
    // instance, so `displayInstance` applies to one segment and not the other.
    // Exactly what `signalCatalog.composeLabel` does for the picker row of the
    // same signal.
    //
    // This label is the FALLBACK, not the expectation: generation only fans this
    // family over channels whose LABEL says they are a bilge float, so the
    // labelled name ("Aft bilge") normally wins above. It exists for the alarm
    // an owner pinned by hand off the signal picker, where "Bilge high water —
    // Switch bank 2 channel 4" is the same phrase the picker showed them.
    case "switch": {
      if (seg.length !== SWITCH_CHANNEL_SEGMENTS || seg[0] !== "electrical" || seg[1] !== "switches" || seg[2] !== "bank" || seg[5] !== "state") {
        return void 0;
      }
      const bank = instanceNoun(seg[3], (n) => n, titleize);
      return `Switch bank ${bank} channel ${seg[4]}`;
    }
    // security.cameras.<id>.<leaf> — the id is Protect's 24-hex object id,
    // which names nothing to an owner. The camera's own name arrives as
    // `displayName` (core reads it off `security.cameras.<id>.name`); without
    // it the title stays the base one rather than "Camera 67bdf865…".
    case "camera":
      return void 0;
    default:
      return void 0;
  }
}
function instanceTitle(baseTitle, family, path, opts = {}) {
  const base = baseTitle.trim();
  const label = instanceLabel(family, path, opts);
  if (label === void 0) return base;
  if (base.length === 0) return label.slice(0, MAX_INSTANCE_TITLE_LEN);
  const full = `${base}${INSTANCE_TITLE_SEPARATOR}${label}`;
  if (full.length <= MAX_INSTANCE_TITLE_LEN) return full;
  const room = MAX_INSTANCE_TITLE_LEN - base.length - INSTANCE_TITLE_SEPARATOR.length;
  if (room <= 1) return base;
  return `${base}${INSTANCE_TITLE_SEPARATOR}${label.slice(0, room - 1).trimEnd()}\u2026`;
}

// src/life-safety.ts
var LIFE_SAFETY_RECIPE_BASES = [
  "bilge_high",
  "bilge_pump_too_long",
  "bilge_pump_cycling",
  "smoke_co_gas",
  "water_in_fuel",
  "hatch_security",
  // TOM-824: a person aboard an unattended boat, and a smoke or CO alarm,
  // siren or breaking glass a camera hears.
  "camera_person",
  "camera_audio_alarm",
  "anchor_drift",
  "anchor_depth_low",
  "deep_depth",
  "gps_loss"
];
var LIFE_SAFETY_RECIPE_SET = new Set(
  LIFE_SAFETY_RECIPE_BASES
);
var LIFE_SAFETY_WORD = /(?<![a-z])(bilge|high[\s._-]?water|flood(?:ing)?|smoke|fire|co|carbon[\s._-]?monoxide|gas|lpg|propane)(?![a-z])/i;
function lifeSafetyReason(subject) {
  const recipeBase = parseRecipeId(subject.recipeId).base;
  if (LIFE_SAFETY_RECIPE_SET.has(recipeBase))
    return { kind: "recipe", recipeBase };
  if (subject.severity === "emergency") return { kind: "severity" };
  if (subject.recipeId.startsWith(CUSTOM_RECIPE_PREFIX)) {
    for (const text of [subject.title, ...subject.labels ?? []]) {
      if (typeof text !== "string") continue;
      const hit = LIFE_SAFETY_WORD.exec(text);
      if (hit !== null) return { kind: "label", word: hit[1].toLowerCase() };
    }
  }
  return null;
}
function isLifeSafetyAlarm(subject) {
  return lifeSafetyReason(subject) !== null;
}

// src/battery-voltage.ts
var SUPPORTED_NOMINAL_VOLTAGES = [
  12,
  24,
  48
];
var RECIPE_THRESHOLD_NOMINAL_VOLTAGE = 12;
var NOMINAL_12V_BAND_VOLTS = { min: 10, max: 16 };
function round22(n) {
  return Math.round(n * 100) / 100;
}
function nominalVoltageForVolts(volts) {
  if (typeof volts !== "number" || !Number.isFinite(volts)) return void 0;
  for (const nominal of SUPPORTED_NOMINAL_VOLTAGES) {
    const factor = nominal / RECIPE_THRESHOLD_NOMINAL_VOLTAGE;
    if (volts >= NOMINAL_12V_BAND_VOLTS.min * factor && volts <= NOMINAL_12V_BAND_VOLTS.max * factor) {
      return nominal;
    }
  }
  return void 0;
}
function nominalVoltageFromReported(value) {
  let volts;
  if (typeof value === "number" && Number.isFinite(value)) volts = value;
  else if (typeof value === "string") {
    const match = /^\s*(\d+(?:\.\d+)?)\s*v?\s*$/i.exec(value);
    if (match) volts = Number(match[1]);
  }
  if (volts === void 0) return void 0;
  return SUPPORTED_NOMINAL_VOLTAGES.find((n) => n === volts);
}
function scaleVoltsToNominal(volts12, nominal) {
  return round22(volts12 * nominal / RECIPE_THRESHOLD_NOMINAL_VOLTAGE);
}
function correctVoltsForNominal(volts, nominal) {
  const written = nominalVoltageForVolts(volts);
  if (written === void 0 || written === nominal) return volts;
  return round22(volts * nominal / written);
}
var BATTERY_VOLTAGE_PATH = /^electrical\.batteries\.([^.]+)\.voltage$/;
function batteryNominalVoltagePath(voltagePath) {
  const match = BATTERY_VOLTAGE_PATH.exec(voltagePath);
  return match ? `electrical.batteries.${match[1]}.nominalVoltage` : void 0;
}
function batteryChemistryFromReported(value) {
  if (typeof value !== "string") return void 0;
  const v = value.trim().toLowerCase();
  if (v === "li" || v.startsWith("lithium") || v.includes("lifepo4")) {
    return "lithium";
  }
  if (v === "pb" || v.startsWith("pb ") || v.startsWith("lead") || v === "flooded" || v === "gel" || v === "agm") {
    return "lead_acid";
  }
  return void 0;
}
var BATTERY_VOLTS_12V = {
  battery_low: { lead_acid: 12, lithium: 12.6 },
  battery_critical: { lead_acid: 11.5, lithium: 12.2 },
  house_battery_low: { lead_acid: 12, lithium: 12.6 },
  house_battery_critical: { lead_acid: 11.5, lithium: 12.2 }
};
function fitBatteryVolts(base, volts, nominal, chemistry) {
  const corrected = correctVoltsForNominal(volts, nominal);
  const defaults = BATTERY_VOLTS_12V[base];
  if (chemistry === void 0 || defaults === void 0) return corrected;
  const other = chemistry === "lithium" ? "lead_acid" : "lithium";
  if (corrected === scaleVoltsToNominal(defaults[other], nominal)) {
    return scaleVoltsToNominal(defaults[chemistry], nominal);
  }
  return corrected;
}
function batteryPrefixOf(voltagePath) {
  const match = BATTERY_VOLTAGE_PATH.exec(voltagePath);
  return match ? `electrical.batteries.${match[1]}` : void 0;
}
var BATTERY_SOC_RECIPE_IDS = [
  "battery_soc_low",
  "battery_soc_critical"
];
function isBatterySocRecipe(base) {
  return BATTERY_SOC_RECIPE_IDS.includes(base);
}
var BATTERY_SOC_PATH = /^electrical\.batteries\.([^.]+)\.capacity\.stateOfCharge$/;
function batteryVoltagePathForSoc(socPath) {
  const match = BATTERY_SOC_PATH.exec(socPath);
  return match ? `electrical.batteries.${match[1]}.voltage` : void 0;
}

// src/detector-labels.ts
var NOT_A_SENSOR = "power|pwr|breaker|brkr|supply|feed|circuit|cct|cb|fuse|fused|lights?|lamps?|leds?";
var SMOKE_CO_GAS_DETECTOR_LABEL = new RegExp(
  `^(?!.*\\b(?:${NOT_A_SENSOR}|fans?|heaters?|blowers?|extractors?|vents?|solenoids?|valves?|shut[\\s_-]?offs?|relays?|regulators?|actuators?|igniters?|burners?)\\b).*(?:\\bsmoke\\b|carbon[\\s_-]?monoxide|\\bco\\b(?![\\s_-]*pilot)|(?:propane|lpg|gas|fumes?)[\\s_-]?(?:detect|alarm|sniff|sensor|leak))`,
  "i"
);
var HATCH_SECURITY_LABEL = new RegExp(
  `^(?!.*\\b(?:${NOT_A_SENSOR}|locks?|lift|lifts|lifters?|actuators?|fans?|heaters?|blowers?|vents?|bells?|motors?|pumps?|rams?|winch(?:es)?|wipers?|blinds?|shades?|defrost(?:ers?)?|demist(?:ers?)?|seals?|inflat(?:e|es|ed|ion|able))\\b).*\\b(?:hatch(?:es)?|doors?|windows?|security|intrusion|motion)\\b`,
  "i"
);

// src/index.ts
var legacyCompiledRuleSchema = z3.object({
  id: z3.string(),
  when: z3.record(z3.string(), z3.unknown()),
  fire: z3.literal("triggered")
});
var legacyCompiledRulesetSchema = z3.object({
  version: z3.number().int().positive(),
  rules: z3.array(legacyCompiledRuleSchema)
});
var notificationChannelSchema = z3.enum([
  "n2k",
  "audible",
  "email",
  "sms",
  "whatsapp",
  "imessage"
]);
var notificationRouteSchema = z3.object({
  channel: notificationChannelSchema
});
var compiledModeSchema = z3.object({
  id: z3.string(),
  name: z3.string().optional()
});
var compiledRuleSchema = z3.object({
  alertId: z3.string(),
  inputPath: z3.string(),
  class: z3.string(),
  trigger: z3.record(z3.string(), z3.unknown()),
  condition: z3.unknown().optional(),
  enabledModes: z3.array(z3.string()),
  setDelaySeconds: z3.number(),
  clearDelaySeconds: z3.number(),
  reactivationPeriodSeconds: z3.number(),
  temporarySilencePeriodSeconds: z3.number(),
  /**
   * INERT since ADR-086, and kept only so `rulesDigest` does not churn.
   *
   * It used to gate the Awaiting Acknowledge state and swallow acknowledges.
   * Both are forbidden: NMEA 2000 Appendix D §9.6.1.2 (PDF 1384, amendment
   * AT20250724) makes Awaiting Acknowledge mandatory "for all alerts except
   * Caution Alerts", and the wire field it mirrored (126983 field 14 / DD325)
   * is now NMEA Reserved (PDF 1396). Severity — `alertType` — is the only
   * sanctioned branch. `digest.ts` hashes the whole rule object, so deleting
   * this field would change every boat's digest and force a one-time ruleset
   * re-push and re-apply.
   */
  acknowledgeSupport: z3.boolean(),
  temporarySilenceSupport: z3.boolean(),
  notificationRoutes: z3.array(notificationRouteSchema).optional(),
  priority: z3.number().optional()
}).passthrough();
var compiledRulesetSchema = z3.object({
  rules: z3.array(compiledRuleSchema),
  modes: z3.array(compiledModeSchema),
  keysSchemaVersion: z3.number().int().positive().optional()
}).passthrough();
export {
  BATTERY_SOC_RECIPE_IDS,
  BATTERY_VOLTS_12V,
  BILGE_PIN_REQUIRED_REASON,
  BILGE_PUMP_PIN_REQUIRED_REASON,
  CANONICAL_MODES,
  CHANNEL_ID_REQUIRED_REASON,
  COMPILABLE_RECIPE_IDS,
  COMPILER_OUTPUT_VERSION,
  CONTACT_ALARM_STATE_CONFLICT,
  CONTACT_ALARM_STATE_KEY,
  CONTACT_ALARM_STATE_MUST_MATCH,
  CONTACT_ALARM_STATE_REASON,
  CREATE_ON_CHANNEL_ERROR,
  CUSTOM_RECIPE_PREFIX,
  CUSTOM_TRIGGER_SPECS,
  DEFAULT_ENGINE_RUNNING_RPM,
  DEPRECATED_RECIPE_IDS,
  ENGINE_RUNNING_RPM,
  ENGINE_SPEED_RESERVED_FLOOR_RPM,
  ENGINE_TURNING_RPM,
  FAN_OUT_RECIPE_IDS,
  HATCH_SECURITY_LABEL,
  HATCH_SECURITY_PIN_REQUIRED_REASON,
  INSTANCE_TITLE_SEPARATOR,
  KPA_TO_HPA,
  LIFE_SAFETY_RECIPE_BASES,
  MAX_CYCLE_WINDOW_SECONDS,
  MAX_INSTANCE_TITLE_LEN,
  MAX_RECIPE_ID_LEN,
  MIN_CORE_FOR_ANCHOR_DEPTH,
  MIN_CORE_FOR_BATTERY_SOC,
  MIN_CORE_FOR_CAMERAS,
  MIN_CORE_FOR_CYCLE_COUNT,
  MIN_CORE_FOR_DETECTOR_CONTACTS,
  MIN_CORE_FOR_SPLIT_RECIPES,
  N2K_ALERT_TYPE,
  N2K_CATEGORY_NAVIGATIONAL,
  N2K_CATEGORY_TECHNICAL,
  NAVIGATIONAL_RECIPE_IDS,
  NEOMARIS_MONITOR_ALERT_SYSTEM,
  NOMINAL_12V_BAND_VOLTS,
  PIN_REQUIRED_RECIPE_IDS,
  REACTIVATION_PERIOD_SECONDS_BY_SEVERITY,
  RECIPE_IDS,
  RECIPE_SPECS,
  RECIPE_THRESHOLD_NOMINAL_VOLTAGE,
  RECIPE_WITHDRAWN_ERROR,
  SMOKE_CO_GAS_DETECTOR_LABEL,
  SMOKE_CO_GAS_PIN_REQUIRED_REASON,
  STORE_UNIT_PLACEHOLDER,
  SUPPORTED_NOMINAL_VOLTAGES,
  TEMPORARY_SILENCE_PERIOD_SECONDS_BY_SEVERITY,
  UNIDENTIFIED_TANK_LABEL,
  WITHDRAWN_RECIPE_IDS,
  alertBindingSchema,
  alertConditionClauseSchema,
  alertConditionsSchema,
  alertDefinitionSchema,
  alertOriginSchema,
  alertSeveritySchema,
  bareChannelIdRefusal,
  batteryChemistryFromReported,
  batteryNominalVoltagePath,
  batteryPrefixOf,
  batteryVoltagePathForSoc,
  canonicalJson,
  compileCustom,
  compileDefinitions,
  compiledModeSchema,
  compiledRuleSchema,
  compiledRulesetSchema,
  correctVoltsForNominal,
  customRecipeIdFor,
  customSpecSchema,
  customTriggerClassSchema,
  defaultConditionsForRecipe,
  defsDigest,
  engineRevolutionsPath,
  fitBatteryVolts,
  foldDefinitionModes,
  foldModeKeys,
  foldModesToCanonical,
  foldSailingOntoUnderway,
  fromDisplayValue,
  instanceLabel,
  instanceTitle,
  isBareChannelIdOnly,
  isBatterySocRecipe,
  isContactStateParam,
  isCustomDef,
  isDeprecatedRecipe,
  isEngineRunning,
  isEngineTurning,
  isLifeSafetyAlarm,
  isReportedEngineSpeed,
  isWithdrawnRecipe,
  legacyCompiledRuleSchema,
  legacyCompiledRulesetSchema,
  lifeSafetyReason,
  mintRecipeId,
  modesAreCanonical,
  n2kAlertIdentity,
  nominalVoltageForVolts,
  nominalVoltageFromReported,
  normaliseContactAlarmState,
  notificationChannelSchema,
  notificationRouteSchema,
  notificationUrgencySchema,
  parseRecipeId,
  recipeAsCustom,
  recipeChannelIdOnly,
  recipeConditionSupport,
  recipeDefaultSeverity,
  recipeInstanceFamily,
  recipeMinCoreVersion,
  recipeNominalVoltageKeys,
  recipePathFamily,
  recipePinRequiredReason,
  recipeReplaces,
  recipeRequiresPin,
  recipeRevivedWithNewArm,
  recipeTakesContactPolarity,
  resolveCondition,
  retiredDefIdentitySchema,
  rulesDigest,
  scaleVoltsToNominal,
  sha256Hex,
  sharedContactAlarmState,
  sortKeysDeep,
  toDisplayValue,
  withdrawnEditRefusal,
  withdrawnRecipeMessage,
  withdrawnRefusal,
  withoutContactAlarmState
};
